import { createFileRoute } from "@tanstack/react-router";
import { IMAGES } from "@/lib/images";
import {
  GuideHero,
  GuideBody,
  GuideFaq,
  GuideCta,
  GuideRelated,
  GuideInlineCta,
  CheckList,
  KeyStats,
  MythVsFact,
  Callout,
  ResponsiveTable,
  buildGuideHead,
  type GuideMeta,
} from "@/components/GuideLayout";

const meta: GuideMeta = {
  category: "Säkerhet",
  title: "Säkerhetsfilm — inbrottsskydd, splitterskydd och EN 12600 / AFS 2020:1",
  tldr:
    "Säkerhetsfilm är en kraftig polyester­film som limmas på glasets insida och håller glaset på plats även när det krossas. 3M Ultra-serien fördröjer inbrottsförsök, skyddar mot personskador från glassplitter och uppfyller pendeltestet i EN 12600 samt kraven i AFS 2020:1 för arbetsplatser. BBN Nordic är auktoriserad installatör i Skåne och Göteborg.",
  readTimeMin: 10,
  heroImage: IMAGES.sakerhetsfilmHero,
  path: "/guider/sakerhetsfilm-inbrottsskydd",
  updated: "2026-06-16",
  faqs: [
    {
      q: "Stoppar säkerhetsfilm en inbrottstjuv helt?",
      a: "Nej — film är fördröjningsskydd, inte penetrationsskydd som pansarglas. Däremot kan en 3M Ultra-film fördröja ett inbrottsförsök med flera minuter, vilket räcker för att en larmad tjuv ska avbryta. De flesta opportunistiska inbrott avbryts vid första misslyckade slag.",
    },
    {
      q: "Vad skiljer 3M Ultra från vanlig säkerhetsfilm?",
      a: "Ultra-serien är uppbyggd av 42 mikroskopiska lager polyester istället för 2–4 lager som standardfilm. Det gör filmen samtidigt mycket tunnare (8 mil ≈ 200 μm) och betydligt segare — den töjs istället för att brista, vilket är exakt det beteende som krävs för att hålla ihop ett krossat glas.",
    },
    {
      q: "Vad säger AFS 2020:1 om glas på arbetsplatser?",
      a: "Arbetsmiljöverkets föreskrift AFS 2020:1 kräver att glas i arbetslokaler ska vara skyddat så att personer inte skadas vid kross — typiskt genom härdat glas, laminerat glas eller godkänd säkerhetsfilm. Säkerhetsfilm är ofta det enda retrofit-alternativet i befintliga byggnader.",
    },
    {
      q: "Var krävs säkerhetsfilm enligt EN 12600?",
      a: "EN 12600 är pendeltestet som klassar hur glas beter sig vid stöt med människokropp. Filmen kan höja en standardrutas klassning till 2B2 eller bättre, vilket är vanligt krav i skolor, daghem, sjukhus, hotell, restauranger och kommersiella entréer.",
    },
    {
      q: "Kan säkerhetsfilm kombineras med solfilm?",
      a: "Ja. 3M Ultra Prestige kombinerar säkerhets- och solskyddsegenskaper i samma film, vilket är vanligt på kontor och hotell där man både vill ha värmereduktion och splitterskydd.",
    },
    {
      q: "Hur tjock är säkerhetsfilm och syns den på glaset?",
      a: "3M Ultra är 200 mikrometer (μm) tjock i klar variant. På insidan av glaset är den i princip osynlig — endast vid kantbelysning kan man ana den. Den ändrar inte glasets färg och har inget mätbart spegelutseende.",
    },
  ],
  related: [
    { to: "/sakerhetsfilm", label: "Säkerhetsfilm — översikt (inkl. 3M Ultra)" },
    { to: "/sakerhetsfilm/solar-screen", label: "Solar Screen — datablad" },
    { to: "/ordlista", label: "Ordlista — EN 12600, AFS, μm och fler termer" },
  ],
};

export const Route = createFileRoute("/guider/sakerhetsfilm-inbrottsskydd")({
  head: () => buildGuideHead(meta),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      <GuideHero meta={meta} />
      <GuideBody>
        <KeyStats
          items={[
            { value: "42", label: "Polyesterlager", sub: "3M Ultra-uppbyggnad" },
            { value: "200 μm", label: "Tjocklek", sub: "klar, optiskt neutral" },
            { value: "EN 12600", label: "Pendeltest", sub: "klassning 2B2+" },
            { value: "10 år", label: "Produktgaranti", sub: "auktoriserad installation" },
          ]}
        />

        <h2>Vad säkerhetsfilm faktiskt är</h2>
        <p>
          Säkerhetsfilm är en kraftig polyesterfilm som limmas på glasets insida med
          industrilim. Den ändrar inte glasets utseende men gör glaset segare och håller det
          på plats även när det är krossat — precis som det laminerade säkerhetsglas som sitter
          i vindrutan på en bil.
        </p>
        <p>
          Det viktiga är att glaset inte är förstärkt mot anslag — det är fortfarande lika
          spräckligt — men det <em>splittrar inte</em>. Är glaset spräckt sitter det kvar i
          karmen, och en inbrottstjuv måste slå sig in genom filmen, vilket tar tid och låter.
        </p>

        <GuideInlineCta />
        <h2>Vanliga missuppfattningar</h2>
        <MythVsFact
          items={[
            {
              myth: "Säkerhetsfilm är samma sak som pansarglas.",
              fact:
                "Nej. Pansarglas stoppar penetration helt; säkerhetsfilm fördröjer. De flesta inbrott är opportunistiska och avbryts vid första missade slaget — film räcker då gott och väl, till en bråkdel av kostnaden.",
            },
            {
              myth: "Filmen syns på glaset och gör rummet mörkt.",
              fact:
                "3M Ultra Clear är optiskt neutral och i princip osynlig från insidan. VLT ligger på 88–90 %, vilket är nästan samma som vanligt klart glas.",
            },
            {
              myth: "Man kan sätta filmen själv för att spara pengar.",
              fact:
                "Felaktig installation (smuts under filmen, för smal kantmarginal, fel typ av lim) gör att hela skyddet inte fungerar och att garantin upphör. 3M kräver auktoriserad installatör för att garantin ska gälla.",
            },
          ]}
        />

        <h2>Två huvudsyften: skydda människor och fördröja inbrott</h2>
        <h3>1. Skydda människor från glassplitter</h3>
        <p>
          Krossat fönsterglas är en betydande skaderisk på arbetsplatser, i skolor, butiker
          och offentliga miljöer. Säkerhetsfilm håller glassbitarna på plats istället för att
          de regnar in eller faller ut. Detta är det vanligaste skälet att filma glas i
          publika miljöer och regleras av Arbetsmiljöverkets föreskrift{" "}
          <strong>AFS 2020:1</strong>.
        </p>
        <h3>2. Fördröja inbrott</h3>
        <p>
          En 3M Ultra-film fördröjer ett inbrottsförsök med flera minuter. För de flesta
          inbrott — som är opportunistiska — räcker första missade slaget för att avbryta
          försöket. För objekt med larm och kameror räcker fördröjningen för väktare eller
          polis att hinna fram.
        </p>

        <GuideInlineCta />
        <h2>Regelverk — kort och konkret</h2>
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Standard</th>
              <th>Vad den reglerar</th>
              <th>När säkerhetsfilm används</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>EN 12600</td>
              <td>Pendeltest av glas vid mänsklig stöt — klass 1, 2 eller 3.</td>
              <td>För att höja klassningen i skolor, hotell, butiker, kontor.</td>
            </tr>
            <tr>
              <td>EN 356</td>
              <td>Manuell attack — klass P1A–P5A.</td>
              <td>Butiker, banker, känsliga objekt med ökat inbrottsskydd.</td>
            </tr>
            <tr>
              <td>AFS 2020:1</td>
              <td>Arbetsplatsens utformning — glas i arbetslokaler.</td>
              <td>Vanligaste skälet i Sverige för säkerhetsfilm i befintlig byggnad.</td>
            </tr>
            <tr>
              <td>BBR 8:35</td>
              <td>Skydd mot personskador, glas över 0,6 m².</td>
              <td>Vid om- och tillbyggnad där glaset inte uppfyller kraven.</td>
            </tr>
          </tbody>
        </ResponsiveTable>


        <h2>3M Ultra — varför 42 lager?</h2>
        <p>
          Standardfilm är uppbyggd av 2–4 polyesterlager och spricker upp i samma riktning
          som glaset. 3M Ultra-serien har <strong>42 mikroskopiska lager</strong> som ligger i
          växlande orientering — det gör filmen samtidigt mycket tunnare och betydligt segare,
          eftersom sprickor inte kan propagera rakt igenom utan måste byta riktning vid varje
          lager.
        </p>
        <p>
          Resultatet är en film som är klar, tunn (200 μm), inte mörkar rummet, men håller
          ihop ett krossat glas mycket effektivt.
        </p>

        <Callout variant="warning" title="Anchor-system krävs vid förhöjd hotbild">
          <p>
            För objekt som banker, guldsmeder, fastigheter med explosionsrisk eller där
            EN 356 klass P4A/P5A krävs ska filmen förankras mot karmen med
            <strong> 3M Impact Protection Attachment</strong> (strukturlim eller mekanisk
            profil). Utan förankring tvingas hela glaset ut ur karmen vid kraftig attack —
            önskvärt vid explosion, men inte alltid vid inbrott.
          </p>
        </Callout>

        <GuideInlineCta />
        <h2>Var används säkerhetsfilm mest?</h2>
        <CheckList
          items={[
            "Skolor, daghem, sjukhus — krav på personskadeskydd.",
            "Butiker och bankkontor — inbrottsskydd vid skyltfönster.",
            "Kontorslobbyer och receptioner med stora glas.",
            "Bostadsrätt och villa — fönster i markplan, altandörrar.",
            "Hotell — kombinerat med solfilm i 3M Ultra Prestige.",
            "Industri och fastighetsbolag — riskinventering enligt AFS.",
          ]}
        />

        <h2>Installation och garanti</h2>
        <p>
          Säkerhetsfilm monteras alltid av auktoriserad installatör — felaktig installation
          (luftbubblor, smuts under filmen, för smal kant) påverkar både prestanda och
          garanti. BBN Nordic är auktoriserad 3M Ultra-installatör och lämnar
          <strong> 10 års produktgaranti</strong> plus 5 års arbetsgaranti.
        </p>
      </GuideBody>
      <GuideFaq items={meta.faqs ?? []} />
      <GuideCta />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
